import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Assessment as QFBRIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Warning as WarningIcon,
  TrendingUp as TrendIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const QFBRReport = () => {
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    die_no: '',
    customer_code: '',
    defect_type: 'all',
    severity: 'all'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['qfbrReport', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/reports/qfbr?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching QFBR report: ${error.message}`, { variant: 'error' });
      },
      enabled: false
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'major': return 'warning';
      case 'minor': return 'info';
      default: return 'default';
    }
  };

  const getDefectRateColor = (rate) => {
    if (rate < 2) return 'success';
    if (rate < 5) return 'warning';
    return 'error';
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading QFBR report: {error.message}
        </Alert>
      </Box>
    );
  }

  // Mock QFBR (Quality Feedback Report) data
  const mockData = [
    {
      qfbr_no: 'QFBR/24/001',
      qfbr_date: '2024-11-15',
      die_no: 'D12345',
      part_name: 'Connecting Rod',
      customer_code: '01',
      customer_name: 'BAJAJ AUTO LIMITED',
      defect_type: 'Dimensional Variation',
      defect_description: 'Bore diameter out of tolerance by +0.05mm',
      severity: 'major',
      quantity_affected: 25,
      total_quantity: 500,
      defect_rate: 5.0,
      root_cause: 'Tool wear in boring operation',
      corrective_action: 'Tool replacement and recalibration',
      preventive_action: 'Implement tool wear monitoring',
      status: 'closed',
      responsible_person: 'QC Manager',
      closure_date: '2024-11-20'
    },
    {
      qfbr_no: 'QFBR/24/002',
      qfbr_date: '2024-11-18',
      die_no: 'D67890',
      part_name: 'Crankshaft',
      customer_code: '02',
      customer_name: 'BHARAT FORGE LTD',
      defect_type: 'Surface Defect',
      defect_description: 'Surface roughness exceeding Ra 6.3',
      severity: 'minor',
      quantity_affected: 8,
      total_quantity: 200,
      defect_rate: 4.0,
      root_cause: 'Improper cutting fluid application',
      corrective_action: 'Cutting fluid system maintenance',
      preventive_action: 'Daily cutting fluid quality checks',
      status: 'open',
      responsible_person: 'Production Manager',
      closure_date: null
    },
    {
      qfbr_no: 'QFBR/24/003',
      qfbr_date: '2024-11-10',
      die_no: 'D11111',
      part_name: 'Gear Blank',
      customer_code: '03',
      customer_name: 'TATA MOTORS LIMITED',
      defect_type: 'Heat Treatment',
      defect_description: 'Hardness variation beyond specified range',
      severity: 'critical',
      quantity_affected: 45,
      total_quantity: 300,
      defect_rate: 15.0,
      root_cause: 'Furnace temperature fluctuation',
      corrective_action: 'Furnace PID controller replacement',
      preventive_action: 'Weekly furnace calibration',
      status: 'closed',
      responsible_person: 'HT Manager',
      closure_date: '2024-11-25'
    },
    {
      qfbr_no: 'QFBR/24/004',
      qfbr_date: '2024-11-22',
      die_no: 'D22222',
      part_name: 'Valve Component',
      customer_code: '04',
      customer_name: 'MAHINDRA & MAHINDRA',
      defect_type: 'Material Defect',
      defect_description: 'Inclusion found in material cross-section',
      severity: 'major',
      quantity_affected: 12,
      total_quantity: 150,
      defect_rate: 8.0,
      root_cause: 'Raw material quality issue',
      corrective_action: 'Material supplier audit',
      preventive_action: 'Enhanced incoming material inspection',
      status: 'in_progress',
      responsible_person: 'QA Manager',
      closure_date: null
    }
  ];

  const totalQFBRs = mockData.length;
  const openQFBRs = mockData.filter(item => item.status === 'open' || item.status === 'in_progress').length;
  const closedQFBRs = mockData.filter(item => item.status === 'closed').length;
  const criticalQFBRs = mockData.filter(item => item.severity === 'critical').length;
  const avgDefectRate = mockData.reduce((sum, item) => sum + item.defect_rate, 0) / mockData.length;

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <QFBRIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          QFBR (Quality Feedback Report)
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Die Number"
              value={filters.die_no}
              onChange={(e) => setFilters({ ...filters, die_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Customer Code"
              value={filters.customer_code}
              onChange={(e) => setFilters({ ...filters, customer_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity}
                label="Severity"
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              >
                <MenuItem value="all">All Severity</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="major">Major</MenuItem>
                <MenuItem value="minor">Minor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {totalQFBRs}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total QFBRs
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {openQFBRs}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Open/In Progress
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="error.main">
                    {criticalQFBRs}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Critical Issues
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.main">
                    {avgDefectRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Avg Defect Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* QFBR Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Quality Feedback Reports (QFBR)
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Period: {filters.from_date} to {filters.to_date}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>QFBR Details</strong></TableCell>
                    <TableCell><strong>Customer & Part</strong></TableCell>
                    <TableCell><strong>Defect Information</strong></TableCell>
                    <TableCell align="center"><strong>Severity</strong></TableCell>
                    <TableCell align="center"><strong>Defect Rate</strong></TableCell>
                    <TableCell><strong>Root Cause</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No QFBR data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mockData.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {record.qfbr_no}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Date: {new Date(record.qfbr_date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              Die: {record.die_no}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.customer_name}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Code: {record.customer_code}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              Part: {record.part_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.defect_type}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {record.defect_description}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Affected: {record.quantity_affected}/{record.total_quantity} pcs
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={record.severity.toUpperCase()}
                            color={getSeverityColor(record.severity)}
                            size="small"
                            icon={record.severity === 'critical' ? <WarningIcon /> : null}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ width: '60px', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(record.defect_rate * 5, 100)} 
                                color={getDefectRateColor(record.defect_rate)}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                            <Typography 
                              variant="body2" 
                              color={getDefectRateColor(record.defect_rate)}
                              fontWeight="bold"
                            >
                              {record.defect_rate.toFixed(1)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="small">
                            {record.root_cause}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="caption" display="block">
                              <strong>Corrective:</strong> {record.corrective_action}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              <strong>Preventive:</strong> {record.preventive_action}
                            </Typography>
                            <Typography variant="caption" display="block" color="info.main">
                              <strong>Owner:</strong> {record.responsible_person}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Chip 
                              label={record.status.replace('_', ' ').toUpperCase()}
                              color={
                                record.status === 'closed' ? 'success' :
                                record.status === 'in_progress' ? 'warning' : 'error'
                              }
                              size="small"
                              icon={
                                record.status === 'closed' ? <PassIcon /> :
                                record.status === 'in_progress' ? <TrendIcon /> : <FailIcon />
                              }
                            />
                            {record.closure_date && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Closed: {new Date(record.closure_date).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* QFBR Process Guide */}
          <Paper sx={{ mt: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              QFBR Process Guidelines
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="error.main">Critical Severity:</Typography>
                <Typography variant="body2">
                  • Immediate action required
                  • Production may be halted
                  • Customer notification mandatory
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="warning.main">Major Severity:</Typography>
                <Typography variant="body2">
                  • Action within 24 hours
                  • Containment measures required
                  • Management review needed
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="info.main">Minor Severity:</Typography>
                <Typography variant="body2">
                  • Action within 48 hours
                  • Standard process improvement
                  • Documentation required
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="success.main">Closure Criteria:</Typography>
                <Typography variant="body2">
                  • Root cause identified
                  • Corrective action implemented
                  • Verification completed
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default QFBRReport;